import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from './useApi';

// Hook pour la liste des prestataires
export const usePrestataires = () => {
  const { user, isAuthenticated } = useAuth();
  const { apiCall, API_BASE_URL } = useApi();
  const [prestataires, setPrestataires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPrestataires = useCallback(async () => {
    // Vérifier si l'utilisateur est connecté et prêt
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      setLoading(false);
      return;
    }

    let tenantId;
    try {
      const user = JSON.parse(userData);
      tenantId = user.tenant_id;
    } catch (error) {
      console.error('Erreur parsing user data:', error);
      setLoading(false);
      return;
    }

    if (!tenantId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const data = await apiCall(`/prestataires`);
      console.log('✅ API Success:', data);
      
      setPrestataires(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('❌ API Error:', err);
      setError(err.message);
      setPrestataires([]);
    } finally {
      setLoading(false);
    }
  }, [apiCall, API_BASE_URL]);

  useEffect(() => {
    fetchPrestataires();
  }, []);

  const refetch = useCallback(() => {
    fetchPrestataires();
  }, [fetchPrestataires]);

  return {
    prestataires,
    loading,
    error,
    refetch
  };
};

// Hook pour un prestataire spécifique
export const usePrestataire = (id) => {
  const { apiCall, API_BASE_URL } = useApi();
  const [prestataire, setPrestataire] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPrestataire = useCallback(async () => {
    if (!id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const data = await apiCall(`/prestataires/${id}`);
      console.log('✅ API Success - Prestataire:', data);
      
      setPrestataire(data);
    } catch (err) {
      console.error('❌ API Error - Prestataire:', err);
      setError(err.message);
      setPrestataire(null);
    } finally {
      setLoading(false);
    }
  }, [id, apiCall, API_BASE_URL]);

  useEffect(() => {
    fetchPrestataire();
  }, []);

  const refetch = useCallback(() => {
    fetchPrestataire();
  }, [fetchPrestataire]);

  return {
    prestataire,
    loading,
    error,
    refetch
  };
};

// Hook pour récupérer les documents d'un prestataire
export const usePrestataireDocuments = (prestataireId) => {
  const { apiCall } = useApi();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDocuments = useCallback(async () => {
    if (!prestataireId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const data = await apiCall(`/prestataires/${prestataireId}/documents`);
      console.log('✅ Documents récupérés:', data);
      
      setDocuments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('❌ Erreur récupération documents:', err);
      setError(err.message);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [prestataireId, apiCall]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  return {
    documents,
    loading,
    error,
    refetch: fetchDocuments
  };
};

// Hook pour les employés d'un prestataire
export const usePrestataireEmployees = (prestataireId) => {
  const { apiCall, API_BASE_URL } = useApi();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchEmployees = useCallback(async () => {
    if (!prestataireId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const data = await apiCall(`/prestataires/${prestataireId}/employees`);
      console.log('✅ API Success - Employees:', data);
      
      setEmployees(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('❌ API Error - Employees:', err);
      setError(err.message);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [prestataireId, apiCall, API_BASE_URL]);

  const addEmployee = useCallback(async (employeeData) => {
    try {
      const newEmployee = await apiCall(`/prestataires/${prestataireId}/employees`, {
        method: 'POST',
        body: JSON.stringify(employeeData)
      });

      setEmployees(prev => [...prev, newEmployee]);
      return newEmployee;
    } catch (err) {
      console.error('❌ API Error - Add Employee:', err);
      throw err;
    }
  }, [prestataireId, apiCall]);

  const updateEmployee = useCallback(async (employeeId, employeeData) => {
    try {
      const updatedEmployee = await apiCall(`/prestataires/${prestataireId}/employees/${employeeId}`, {
        method: 'PUT',
        body: JSON.stringify(employeeData)
      });

      setEmployees(prev => prev.map(emp => emp.id === employeeId ? updatedEmployee : emp));
      return updatedEmployee;
    } catch (err) {
      console.error('❌ API Error - Update Employee:', err);
      throw err;
    }
  }, [prestataireId, apiCall]);

  const deleteEmployee = useCallback(async (employeeId) => {
    try {
      await apiCall(`/prestataires/${prestataireId}/employees/${employeeId}`, {
        method: 'DELETE'
      });

      setEmployees(prev => prev.filter(emp => emp.id !== employeeId));
      return true;
    } catch (err) {
      console.error('❌ API Error - Delete Employee:', err);
      throw err;
    }
  }, [prestataireId, apiCall]);

  useEffect(() => {
    fetchEmployees();
  }, []);

  return {
    employees,
    loading,
    error,
    addEmployee,
    updateEmployee,
    deleteEmployee,
    refetch: fetchEmployees
  };
};

// Hook pour les services d'un prestataire
export const usePrestataireServices = (prestataireId) => {
  const { apiCall, API_BASE_URL } = useApi();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchServices = useCallback(async () => {
    if (!prestataireId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const data = await apiCall(`/prestataires/${prestataireId}/services`);
      console.log('✅ API Success - Services:', data);
      
      setServices(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('❌ API Error - Services:', err);
      setError(err.message);
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, [prestataireId, apiCall, API_BASE_URL]);

  const addService = useCallback(async (serviceData) => {
    try {
      const response = await apiCall(`${API_BASE_URL}/prestataires/${prestataireId}/services`, {
        method: 'POST',
        body: JSON.stringify(serviceData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const newService = await response.json();
      setServices(prev => [...prev, newService]);
      
      return newService;
    } catch (err) {
      console.error('❌ API Error - Add Service:', err);
      
      // Simulation pour le développement
      const newService = {
        id: Date.now().toString(),
        ...serviceData,
        active: true
      };
      setServices(prev => [...prev, newService]);
      return newService;
    }
  }, [prestataireId, apiCall, API_BASE_URL]);

  const updateService = useCallback(async (serviceId, serviceData) => {
    try {
      const response = await apiCall(`${API_BASE_URL}/prestataires/${prestataireId}/services/${serviceId}`, {
        method: 'PUT',
        body: JSON.stringify(serviceData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const updatedService = await response.json();
      setServices(prev => prev.map(service => service.id === serviceId ? updatedService : service));
      
      return updatedService;
    } catch (err) {
      console.error('❌ API Error - Update Service:', err);
      
      // Simulation pour le développement
      const updatedService = { id: serviceId, ...serviceData };
      setServices(prev => prev.map(service => service.id === serviceId ? updatedService : service));
      return updatedService;
    }
  }, [prestataireId, apiCall, API_BASE_URL]);

  const deleteService = useCallback(async (serviceId) => {
    try {
      const response = await apiCall(`${API_BASE_URL}/prestataires/${prestataireId}/services/${serviceId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setServices(prev => prev.filter(service => service.id !== serviceId));
      
      return true;
    } catch (err) {
      console.error('❌ API Error - Delete Service:', err);
      
      // Simulation pour le développement
      setServices(prev => prev.filter(service => service.id !== serviceId));
      return true;
    }
  }, [prestataireId, apiCall, API_BASE_URL]);

  useEffect(() => {
    fetchServices();
  }, []);

  return {
    services,
    loading,
    error,
    addService,
    updateService,
    deleteService,
    refetch: fetchServices
  };
};

// Hook pour les missions d'un prestataire
export const usePrestataireMissions = (prestataireId) => {
  const { apiCall } = useApi();
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMissions = useCallback(async () => {
    if (!prestataireId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data = await apiCall(`/missions?prestataire_id=${prestataireId}`);
      console.log('✅ API Success - Missions:', data);

      setMissions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('❌ API Error - Missions:', err);
      setError(err.message);
      setMissions([]);
    } finally {
      setLoading(false);
    }
  }, [prestataireId, apiCall]);

  useEffect(() => {
    fetchMissions();
  }, [fetchMissions]);

  return {
    missions,
    loading,
    error,
    refetch: fetchMissions
  };
};

// Hook pour les paiements d'un prestataire
export const usePrestatairePaiements = (prestataireId) => {
  const { apiCall } = useApi();
  const [paiements, setPaiements] = useState([]);
  const [stats, setStats] = useState({ solde: 0, revenus: 0, commissions: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPaiements = useCallback(async () => {
    if (!prestataireId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data = await apiCall(`/paiements?prestataire_id=${prestataireId}`);
      console.log('✅ API Success - Paiements:', data);

      const paiementsList = Array.isArray(data) ? data : (data.paiements || []);
      setPaiements(paiementsList);

      // Calculer les stats
      const totalBrut = paiementsList.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
      const totalCommissions = paiementsList.reduce((sum, p) => sum + (parseFloat(p.commission) || 0), 0);
      const totalNet = totalBrut - totalCommissions;

      setStats({
        solde: totalNet,
        revenus: totalBrut,
        commissions: totalCommissions
      });
    } catch (err) {
      console.error('❌ API Error - Paiements:', err);
      setError(err.message);
      setPaiements([]);
    } finally {
      setLoading(false);
    }
  }, [prestataireId, apiCall]);

  useEffect(() => {
    fetchPaiements();
  }, [fetchPaiements]);

  return {
    paiements,
    stats,
    loading,
    error,
    refetch: fetchPaiements
  };
};

// Hook pour les évaluations d'un prestataire
export const usePrestataireEvaluations = (prestataireId) => {
  const { apiCall } = useApi();
  const [evaluations, setEvaluations] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchEvaluations = useCallback(async () => {
    if (!prestataireId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Essayer d'abord l'API mobile providers
      let data;
      try {
        data = await apiCall(`/mobile/providers/${prestataireId}/reviews`);
      } catch {
        // Fallback sur l'API signalements/evaluations
        data = await apiCall(`/signalements?prestataire_id=${prestataireId}&type=evaluation`);
      }

      console.log('✅ API Success - Evaluations:', data);

      const evalList = Array.isArray(data) ? data : (data.reviews || data.evaluations || []);
      setEvaluations(evalList);

      // Calculer la moyenne
      if (evalList.length > 0) {
        const avg = evalList.reduce((sum, e) => sum + (e.rating || 0), 0) / evalList.length;
        setAverageRating(Math.round(avg * 10) / 10);
      }
    } catch (err) {
      console.error('❌ API Error - Evaluations:', err);
      setError(err.message);
      setEvaluations([]);
    } finally {
      setLoading(false);
    }
  }, [prestataireId, apiCall]);

  useEffect(() => {
    fetchEvaluations();
  }, [fetchEvaluations]);

  return {
    evaluations,
    averageRating,
    loading,
    error,
    refetch: fetchEvaluations
  };
};

// Hook pour l'abonnement d'un prestataire
export const usePrestataireAbonnement = (prestataireId) => {
  const { apiCall } = useApi();
  const [abonnement, setAbonnement] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAbonnement = useCallback(async () => {
    if (!prestataireId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Récupérer les plans disponibles
      const plansData = await apiCall(`/abonnements`);
      console.log('✅ API Success - Plans:', plansData);
      setPlans(Array.isArray(plansData) ? plansData : []);

      // Récupérer l'abonnement actuel du prestataire (si endpoint existe)
      try {
        const currentSub = await apiCall(`/abonnements?prestataire_id=${prestataireId}`);
        if (currentSub && currentSub.length > 0) {
          setAbonnement(currentSub[0]);
        }
      } catch {
        // Pas d'abonnement actif
        setAbonnement(null);
      }
    } catch (err) {
      console.error('❌ API Error - Abonnement:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [prestataireId, apiCall]);

  useEffect(() => {
    fetchAbonnement();
  }, [fetchAbonnement]);

  return {
    abonnement,
    plans,
    loading,
    error,
    refetch: fetchAbonnement
  };
};